import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { Plus, Check, X, Trash, FilePdf } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { LexicalRichTextEditor } from '@/components/ui/lexical-editor'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useApp } from '@/contexts/AppContext'
import { isQuestionOpen } from '@/contexts/AppContext'
import { useGlobalHotkeys } from '@/hooks/useGlobalHotkeys'
import { Question, Answer, QANode } from '@/types'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ArrowLeftIcon } from 'lucide-react'
import { CopyButton } from '@/components/ui/copy-button'
import { storage } from '@/lib/storage'
import { useDialog } from '@/contexts/DialogContext'
import { exportThreadToPDF } from '@/lib/pdf-export'

interface ThreadPageProps { readOnly?: boolean }

export function ThreadPage({ readOnly = false }: ThreadPageProps) {
  const { entityId, threadId } = useParams<{ entityId: string; threadId: string }>()
  const { state, dispatch, isReady } = useApp()
  const { t } = useTranslation()
  const dialog = useDialog()
  const navigate = useNavigate()
  
  // Alternate mode state
  const [isAlternateMode, setIsAlternateMode] = useState(false)
  const compactPrefLoadedRef = useRef(false)

  // Load persisted compact mode for Thread page
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const saved = await storage.getPreference('ui.threadPage.compact')
        if (!cancelled && typeof saved === 'boolean') {
          setIsAlternateMode(saved)
        }
      } finally {
        if (!cancelled) compactPrefLoadedRef.current = true
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Persist compact mode when it changes
  useEffect(() => {
    if (!compactPrefLoadedRef.current) return
    storage.savePreference('ui.threadPage.compact', isAlternateMode)
  }, [isAlternateMode])
  
  // Registry of save callbacks for currently editing items
  const saveCallbacksRef = useRef(new Set<() => void>())
  // Registry of cancel callbacks for currently editing items
  const cancelCallbacksRef = useRef(new Set<() => void>())
  const [editingActive, setEditingActive] = useState(false)
  const refreshEditingActive = () => setEditingActive(saveCallbacksRef.current.size + cancelCallbacksRef.current.size > 0)
  const registerSaveHandler = useCallback((fn: () => void) => {
    saveCallbacksRef.current.add(fn)
    refreshEditingActive()
  }, [])
  const unregisterSaveHandler = useCallback((fn: () => void) => {
    saveCallbacksRef.current.delete(fn)
    refreshEditingActive()
  }, [])
  const registerCancelHandler = useCallback((fn: () => void) => {
    cancelCallbacksRef.current.add(fn)
    refreshEditingActive()
  }, [])
  const unregisterCancelHandler = useCallback((fn: () => void) => {
    cancelCallbacksRef.current.delete(fn)
    refreshEditingActive()
  }, [])

  const saveAllEdits = useCallback(() => {
    // Execute all registered save callbacks
    for (const fn of Array.from(saveCallbacksRef.current)) {
      try { fn() } catch { /* no-op */ }
    }
  }, [])

  const cancelAllEdits = useCallback(() => {
    // Execute all registered cancel callbacks
    for (const fn of Array.from(cancelCallbacksRef.current)) {
      try { fn() } catch { /* no-op */ }
    }
  }, [])

  // Shortcut handlers (compute state on demand)
  const handleCreateConclusiveAnswer = useCallback(() => {
    if (!threadId) return
    const thread = state.threads[threadId]
    if (!thread) return
    const flatNodes = getFlatNodeList(thread.rootQuestionId, state.qaNodes)
    const lastNode = flatNodes[flatNodes.length - 1]
    if (!lastNode || lastNode.node.type !== 'question') return
    const question = lastNode.node as Question
    if (question.answerId) return
    dispatch({ type: 'CREATE_ANSWER', payload: { questionId: question.id, text: '', status: 'conclusive' } })
  }, [dispatch, state.qaNodes, state.threads, threadId])

  const handleCreateInconclusiveAnswer = useCallback(() => {
    if (!threadId) return
    const thread = state.threads[threadId]
    if (!thread) return
    const flatNodes = getFlatNodeList(thread.rootQuestionId, state.qaNodes)
    const lastNode = flatNodes[flatNodes.length - 1]
    if (!lastNode || lastNode.node.type !== 'question') return
    const question = lastNode.node as Question
    if (question.answerId) return
    dispatch({ type: 'CREATE_ANSWER', payload: { questionId: question.id, text: '', status: 'inconclusive' } })
  }, [dispatch, state.qaNodes, state.threads, threadId])

  const handleCreateFollowupQuestion = useCallback(() => {
    if (!threadId) return
    const thread = state.threads[threadId]
    if (!thread) return
    const flatNodes = getFlatNodeList(thread.rootQuestionId, state.qaNodes)
    const lastNode = flatNodes[flatNodes.length - 1]
    if (!lastNode || lastNode.node.type !== 'answer') return
    const answer = lastNode.node as Answer
    dispatch({ type: 'CREATE_QUESTION', payload: { text: '', parentAnswerId: answer.id } })
  }, [dispatch, state.qaNodes, state.threads, threadId])

  // Setup keyboard shortcuts (unconditional hook, gated by enabled)
  useGlobalHotkeys({
    shortcuts: [
      { key: 'a', modifiers: {}, description: t('shortcutCreateConclusiveAnswer'), action: handleCreateConclusiveAnswer },
      { key: 's', modifiers: {}, description: t('shortcutCreateInconclusiveAnswer'), action: handleCreateInconclusiveAnswer },
      { key: 'd', modifiers: {}, description: t('shortcutCreateFollowupQuestion'), action: handleCreateFollowupQuestion }
    ],
    enabled: !readOnly && (state.settings?.shortcutsEnabled ?? true)
  })

  // Ensure Ctrl+S saves and Ctrl+D cancels even when focus is within an input/textarea
  useEffect(() => {
    if (readOnly) return
    if (!(state.settings?.shortcutsEnabled ?? true)) return
    const handler = (e: KeyboardEvent) => {
      const key = (e.key || '').toLowerCase()
      const isKeyS = key === 's' || e.code === 'KeyS'
      const isKeyD = key === 'd' || e.code === 'KeyD'
      const isSaveCombo = isKeyS && (e.ctrlKey || e.metaKey)
      const isCancelCombo = isKeyD && (e.ctrlKey || e.metaKey)
      if (isSaveCombo || isCancelCombo) {
        e.preventDefault()
        e.stopPropagation()
        if (typeof (e as any).stopImmediatePropagation === 'function') {
          ;(e as any).stopImmediatePropagation()
        }
        if (isSaveCombo) {
          saveAllEdits()
        } else if (isCancelCombo) {
          cancelAllEdits()
        }
      }
    }
    window.addEventListener('keydown', handler, true)
    document.addEventListener('keydown', handler, true)
    return () => {
      window.removeEventListener('keydown', handler, true)
      document.removeEventListener('keydown', handler, true)
    }
  }, [saveAllEdits, cancelAllEdits, readOnly, state.settings?.shortcutsEnabled])


  if (!entityId || !threadId) {
    return <Navigate to={readOnly ? "/archivio" : "/entities"} replace />
  }

  const entity = state.entities[entityId]
  const thread = state.threads[threadId]

  if (!isReady) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">{t('loading') || 'Loading...'}</div>
      </div>
    )
  }

  if (!entity || !thread) {
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1)
      } else {
        const fallback = entityId ? `${readOnly ? '/archivio' : '/entities'}/${entityId}` : (readOnly ? '/archivio' : '/entities')
        navigate(fallback, { replace: true })
      }
    }
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="lg" className="h-10 w-10 p-0" aria-label={t('back')} onClick={handleBack}>
            <ArrowLeftIcon className="size-7" />
          </Button>
          <h1 className="text-lg font-semibold">{t('threadNotFound') || 'Thread not found'}</h1>
        </div>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">
            {t('threadMissingMessage') || 'The requested thread does not exist in the current data.'}
          </p>
        </Card>
      </div>
    )
  }

  const rootQuestion = state.qaNodes[thread.rootQuestionId] as Question
  const updatedDate = new Date(thread.updatedAt).toLocaleDateString()

  // Handle PDF export
  const handleExportPDF = async () => {
    try {
      await exportThreadToPDF({
        thread,
        qaNodes: state.qaNodes,
        entityName: entity.name
      })
    } catch (error) {
      console.error('PDF export failed:', error)
      await dialog.error({ message: 'Failed to export PDF. Please try again.' })
    }
  }

  // Get the last node and question for shortcuts - using existing getFlatNodeList function
  const flatNodes = getFlatNodeList(rootQuestion.id, state.qaNodes)
  const lastNode = flatNodes[flatNodes.length - 1]
  const lastQuestionId = [...flatNodes]
    .reverse()
    .find((n) => n.node.type === 'question')?.node.id
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate(`${readOnly ? '/archivio' : '/entities'}/${entityId}`, { replace: true })
    }
  }
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="lg" className="h-10 w-10 p-0" aria-label={`${t('backTo')} ${entity.name}`} onClick={handleBack}>
          <ArrowLeftIcon className="size-7" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <span>{thread.title}</span>
            <span className="text-sm text-muted-foreground">{t('updated')} {updatedDate}</span>
          </h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-pdf-export hover:text-pdf-export-hover hover:bg-pdf-export/10"
          onClick={handleExportPDF}
          aria-label={t('exportPDF')}
        >
          <FilePdf size={16} className="mr-2" />
          {t('exportPDF')}
        </Button>
      </div>

      {/* Alternate Mode Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <Checkbox 
          id="alternate-mode"
          checked={isAlternateMode}
          onCheckedChange={(checked) => setIsAlternateMode(Boolean(checked))}
        />
        <Label htmlFor="alternate-mode" className="text-sm cursor-pointer select-none">
          {t('alternateMode')}
        </Label>
      </div>

      {/* Inline hint for Ctrl+S / Ctrl+D when edits are active */}
      {/* Shortcut hints moved to Sidebar only */}

      {/* Q&A Content - Switch between normal and alternate mode */}
      <div className="space-y-3">
        {rootQuestion && (
          isAlternateMode ? (
            <CompactQAList
              rootQuestionId={rootQuestion.id}
              qaNodes={state.qaNodes}
              dispatch={dispatch}
              readOnly={readOnly}
              registerSaveHandler={registerSaveHandler}
              unregisterSaveHandler={unregisterSaveHandler}
              registerCancelHandler={registerCancelHandler}
              unregisterCancelHandler={unregisterCancelHandler}
            />
          ) : (
            <FlatQAList
              rootQuestionId={rootQuestion.id}
              qaNodes={state.qaNodes}
              dispatch={dispatch}
              readOnly={readOnly}
              registerSaveHandler={registerSaveHandler}
              unregisterSaveHandler={unregisterSaveHandler}
              registerCancelHandler={registerCancelHandler}
              unregisterCancelHandler={unregisterCancelHandler}
            />
          )
        )}
      </div>
    </div>
  )
}

interface EditableConsequenceRowProps {
  answerId: string
  consequence: { id: string; title: string; completedAt?: string }
  dispatch: React.Dispatch<any>
  readOnly?: boolean
  registerSaveHandler?: (fn: () => void) => void
  unregisterSaveHandler?: (fn: () => void) => void
  registerCancelHandler?: (fn: () => void) => void
  unregisterCancelHandler?: (fn: () => void) => void
}

function EditableConsequenceRow({ answerId, consequence, dispatch, readOnly = false, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: EditableConsequenceRowProps) {
  const { t } = useTranslation()
  const dialog = useDialog()
  const [isEditing, setIsEditing] = useState(!readOnly && !consequence.title)
  const [text, setText] = useState(consequence.title)
  const consequenceTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Move cursor to end when editing consequence
  useEffect(() => {
    if (isEditing && consequenceTextareaRef.current) {
      const textarea = consequenceTextareaRef.current
      const length = textarea.value.length
      textarea.setSelectionRange(length, length)
    }
  }, [isEditing])

  const save = () => {
    if (text.trim() && text !== consequence.title) {
      dispatch({
        type: 'UPDATE_CONSEQUENCE',
        payload: { answerId, consequenceId: consequence.id, title: text.trim() }
      })
    }
    setIsEditing(false)
  }
  const cancel = () => {
    setText(consequence.title)
    setIsEditing(false)
  }

  // Register save handler while editing
  useEffect(() => {
    if (!readOnly && isEditing) {
      const saveCb = () => save()
      const cancelCb = () => cancel()
      if (registerSaveHandler) registerSaveHandler(saveCb)
      if (registerCancelHandler) registerCancelHandler(cancelCb)
      return () => {
        if (unregisterSaveHandler) unregisterSaveHandler(saveCb)
        if (unregisterCancelHandler) unregisterCancelHandler(cancelCb)
      }
    }
  }, [isEditing, text, readOnly, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler])

  return (
    <div className="flex items-center gap-2">
      {!readOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={t('delete') || 'Delete'}
              className="inline-flex h-6 w-6 items-center justify-center p-0.5 text-destructive hover:text-destructive/80 hover:bg-transparent rounded"
              onClick={async () => {
                const confirmed = await dialog.confirm({
                  message: t('confirmDeleteConsequence') || 'Rimuovere questa conseguenza?',
                  type: 'warning'
                })
                if (confirmed) {
                  dispatch({
                    type: 'DELETE_CONSEQUENCE',
                    payload: { answerId, consequenceId: consequence.id }
                  } as any)
                }
              }}
            >
              <Trash size={18} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="px-3.5 py-2 text-sm font-semibold shadow-md">
            {t('tooltipRemoveConsequence')}
          </TooltipContent>
        </Tooltip>
      )}
            <CopyButton value={consequence.title || ''} title={t('copy') || 'Copy'} />
      <Checkbox
        checked={!!consequence.completedAt}
        disabled={readOnly}
        onCheckedChange={(checked) => {
          if (readOnly) return
          dispatch({
            type: 'UPDATE_CONSEQUENCE',
            payload: {
              answerId,
              consequenceId: consequence.id,
              completed: checked as boolean,
            }
          })
        }}
        className="h-4 w-4 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary text-primary-foreground"
      />

      {isEditing && !readOnly ? (
        <div className="flex-1 flex items-center gap-2">
          <Textarea
            ref={consequenceTextareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-[2rem] resize-none field-sizing-content"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                e.preventDefault();
                save()
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel()
              }
            }}
            autoFocus
          />
          <CopyButton value={text || ''} title={t('copy') || 'Copy'} />
          <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={save}>
            <Check size={12} className="mr-1" />
            {t('save')}
          </Button>
          <Button size="sm" className="bg-error text-error-foreground hover:bg-error/90" onClick={cancel}>
            <X size={12} className="mr-1" />
            {t('cancel')}
          </Button>
        </div>
      ) : (
        <div className="flex-1 flex items-center gap-2">
          <div
            className={cn(
              "flex-1 text-sm cursor-pointer hover:bg-accent/20 p-2 rounded",
              consequence.completedAt && "text-muted-foreground"
            )}
            onClick={() => !readOnly && setIsEditing(true)}
          >
            {consequence.title}
          </div>
        </div>
      )}
    </div>
  )
}

// Compact Q&A List Component for alternate mode
interface CompactQAListProps {
  rootQuestionId: string
  qaNodes: Record<string, QANode>
  dispatch: React.Dispatch<any>
  readOnly?: boolean
  registerSaveHandler: (fn: () => void) => void
  unregisterSaveHandler: (fn: () => void) => void
  registerCancelHandler: (fn: () => void) => void
  unregisterCancelHandler: (fn: () => void) => void
}

function CompactQAList({ rootQuestionId, qaNodes, dispatch, readOnly = false, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: CompactQAListProps) {
  const flatNodes = getFlatNodeList(rootQuestionId, qaNodes)
  const { t } = useTranslation()
  
  return (
    <div className="border rounded-lg p-4 bg-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium">{t('compactView') || 'Vista compatta'}</h3>
      </div>
      <div className="space-y-2">
        {flatNodes.map(({ node, level }, index) => (
          <CompactQAItem
            key={node.id}
            node={node}
            level={level}
            index={index}
            qaNodes={qaNodes}
            dispatch={dispatch}
            readOnly={readOnly}
            registerSaveHandler={registerSaveHandler}
            unregisterSaveHandler={unregisterSaveHandler}
            registerCancelHandler={registerCancelHandler}
            unregisterCancelHandler={unregisterCancelHandler}
          />
        ))}
      </div>
    </div>
  )
}

interface CompactQAItemProps {
  node: QANode
  level: number
  index: number
  qaNodes: Record<string, QANode>
  dispatch: React.Dispatch<any>
  readOnly?: boolean
  registerSaveHandler: (fn: () => void) => void
  unregisterSaveHandler: (fn: () => void) => void
  registerCancelHandler: (fn: () => void) => void
  unregisterCancelHandler: (fn: () => void) => void
}

function CompactQAItem({ node, level, index, qaNodes, dispatch, readOnly = false, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: CompactQAItemProps) {
  const { t } = useTranslation()
  const { state } = useApp()
  const dialog = useDialog()
  const verboseMode = state.settings?.verboseMode ?? true
  const [isEditing, setIsEditing] = useState(!readOnly && !node.text)
  const [editText, setEditText] = useState(node.text)
  const [editHtml, setEditHtml] = useState(node.text)
  const [viewHtml, setViewHtml] = useState(node.text)
  const answerNode = node.type === 'answer' ? node as Answer : undefined
  const consequences = answerNode?.consequences ?? []
  const [editingConsequenceId, setEditingConsequenceId] = useState<string | null>(null)
  const [consequenceDraft, setConsequenceDraft] = useState<Record<string, { text: string; completed: boolean }>>({})
  const consequenceTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Move cursor to end when editing consequence
  useEffect(() => {
    if (editingConsequenceId && consequenceTextareaRef.current) {
      const textarea = consequenceTextareaRef.current
      const length = textarea.value.length
      textarea.setSelectionRange(length, length)
    }
  }, [editingConsequenceId])
  
  const handleSaveEdit = () => {
    const payloadHtml = (editHtml || '').trim()
    const hasText = (editText || '').trim().length > 0
    if (hasText) {
      if (node.type === 'question') {
        dispatch({ type: 'UPDATE_QUESTION', payload: { id: node.id, text: payloadHtml } })
      } else {
        dispatch({ type: 'UPDATE_ANSWER', payload: { id: node.id, text: payloadHtml } })
      }
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setEditText(node.text)
    setEditHtml(node.text)
    setViewHtml(node.text)
    setIsEditing(false)
  }

  useEffect(() => {
    if (!readOnly && isEditing) {
      const saveCb = () => handleSaveEdit()
      const cancelCb = () => handleCancelEdit()
      registerSaveHandler?.(saveCb)
      registerCancelHandler?.(cancelCb)
      return () => {
        unregisterSaveHandler?.(saveCb)
        unregisterCancelHandler?.(cancelCb)
      }
    }
  }, [isEditing, editText, readOnly, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler])

  useEffect(() => {
    setEditHtml(node.text)
    setViewHtml(node.text)
    setEditText(() => {
      try { const doc = new DOMParser().parseFromString(node.text || '', 'text/html'); return doc.body.textContent || '' } catch { return node.text || '' }
    })
  }, [node.text])

  const toggleEditing = () => {
    if (!readOnly) {
      setIsEditing(!isEditing)
    }
  }

  const typeLabel = node.type === 'question' ? t('domanda') : t('risposta')
  const indent = level * 16

  const htmlToPlain = (source?: string) => {
    if (!source) return ''
    try {
      const doc = new DOMParser().parseFromString(source, 'text/html')
      return doc.body.textContent || ''
    } catch { return source || '' }
  }

  const toggleConsequenceEditing = (consequenceId: string, startEditing: boolean, initialTitle: string, completed: boolean) => {
    if (readOnly) return
    if (startEditing) {
      setEditingConsequenceId(consequenceId)
      setConsequenceDraft(prev => ({
        ...prev,
        [consequenceId]: { text: initialTitle, completed }
      }))
    } else {
      setEditingConsequenceId(null)
      setConsequenceDraft(prev => {
        if (!(consequenceId in prev)) return prev
        const { [consequenceId]: _omit, ...rest } = prev
        return rest
      })
    }
  }

  const updateConsequenceDraft = (consequenceId: string, updates: Partial<{ text: string; completed: boolean }>) => {
    const original = consequences.find(c => c.id === consequenceId)
    setConsequenceDraft(prev => {
      const prevEntry = prev[consequenceId]
      return {
        ...prev,
        [consequenceId]: {
          text: updates.text ?? prevEntry?.text ?? original?.title ?? '',
          completed: updates.completed ?? prevEntry?.completed ?? Boolean(original?.completedAt)
        }
      }
    })
  }

  const handleConsequenceSave = (consequenceId: string) => {
    const draft = consequenceDraft[consequenceId]
    const original = consequences.find(c => c.id === consequenceId)
    if (!draft) { setEditingConsequenceId(null); return }
    const nextText = (draft.text || '').trim()
    const nextCompleted = !!draft.completed
    const originalCompleted = Boolean(original?.completedAt)
    const payload: Record<string, any> = {}
    if (nextText && nextText !== (original?.title || '')) payload.title = nextText
    if (nextCompleted !== originalCompleted) payload.completed = nextCompleted
    if (Object.keys(payload).length > 0) {
      dispatch({ type: 'UPDATE_CONSEQUENCE', payload: { answerId: node.id, consequenceId, ...payload } } as any)
    }
    setEditingConsequenceId(null)
    setConsequenceDraft(prev => { const { [consequenceId]: _omit, ...rest } = prev; return rest })
  }

  const handleConsequenceCancel = (consequenceId: string) => {
    setEditingConsequenceId(null)
    setConsequenceDraft(prev => { const { [consequenceId]: _omit, ...rest } = prev; return rest })
  }

  // Register save/cancel handlers for consequence edit
  useEffect(() => {
    if (readOnly) return
    if (!editingConsequenceId) return
    const saveCb = () => handleConsequenceSave(editingConsequenceId)
    const cancelCb = () => handleConsequenceCancel(editingConsequenceId)
    registerSaveHandler(saveCb)
    registerCancelHandler(cancelCb)
    return () => {
      unregisterSaveHandler(saveCb)
      unregisterCancelHandler(cancelCb)
    }
  }, [editingConsequenceId, consequenceDraft, readOnly])

  return (
    <div className="flex items-center gap-3" style={{ marginLeft: `${indent}px` }}>
      <Badge 
        variant="outline" 
        className={cn("text-xs font-medium px-2 py-1 shrink-0", 
          node.type === 'question' ? 'text-info border-info-border' : 'text-success border-success-border'
        )}
      >
        {typeLabel}
      </Badge>
      
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <LexicalRichTextEditor
              placeholder={`${typeLabel}...`}
              initialValue={editHtml}
              onChange={(html, plainText) => {
                setEditHtml(html)
                setEditText(plainText)
              }}
              className="min-h-[80px]"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={handleSaveEdit}>
                <Check size={12} className="mr-1" />
                {t('save')}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                <X size={12} className="mr-1" />
                {t('cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="group">
            <div 
              className="text-sm cursor-pointer hover:bg-accent/20 p-2 rounded"
              onClick={toggleEditing}
              dangerouslySetInnerHTML={{ __html: viewHtml }}
            />
            {verboseMode && node.type === 'answer' && (node as Answer).status && (
              <Badge variant="outline" className={cn("text-xs ml-2", 
                (node as Answer).status === 'conclusive' ? 'text-success border-success-border' : 'text-error border-error-border'
              )}>
                {(node as Answer).status === 'conclusive' ? t('conclusive') : t('inconclusive')}
              </Badge>
            )}
          </div>
        )}

        {/* Consequences (compact parity) */}
        {node.type === 'answer' && consequences.length > 0 && (
          <div className="mt-2 ml-4 space-y-2">
            {verboseMode && (
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t('consequences')}</div>
            )}
            {consequences.map(consequence => {
              const consequenceId = consequence.id
              const draft = consequenceDraft[consequenceId]
              const isCompleted = draft?.completed ?? Boolean(consequence.completedAt)
              const isEditingConsequence = editingConsequenceId === consequenceId
              const titleDraft = draft?.text ?? consequence.title ?? ''
              const plainDraft = titleDraft
              const widthCh = Math.min(Math.max(16, plainDraft.length + 1), 72)
              return (
                <div key={consequenceId} className={cn('rounded border px-2 py-1.5 text-[11px] transition-colors', isCompleted ? 'bg-consequence-completed border-consequence-completed-border' : 'bg-consequence-pending border-consequence-pending-border')}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Checkbox
                      checked={isCompleted}
                      disabled={readOnly}
                      onCheckedChange={(checked) => {
                        if (readOnly) return
                        const nextCompleted = Boolean(checked)
                        updateConsequenceDraft(consequenceId, { completed: nextCompleted })
                        dispatch({ type: 'UPDATE_CONSEQUENCE', payload: { answerId: node.id, consequenceId, completed: nextCompleted } } as any)
                      }}
                      className="h-4 w-4"
                    />
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {isEditingConsequence && !readOnly ? (
                        <Textarea
                          ref={consequenceTextareaRef}
                          value={titleDraft}
                          onChange={(e) => updateConsequenceDraft(consequenceId, { text: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) { e.preventDefault(); handleConsequenceSave(consequenceId) }
                            else if (e.key === 'Escape') { e.preventDefault(); handleConsequenceCancel(consequenceId) }
                          }}
                          className="min-h-[2rem] resize-none text-[11px] field-sizing-content"
                          style={{ width: `${widthCh}ch` }}
                          autoFocus
                        />
                      ) : (
                        <div
                          className={cn('rounded px-1 py-0.5 whitespace-pre-wrap', !readOnly && 'cursor-pointer hover:bg-card/60', isCompleted && 'line-through opacity-70')}
                          style={{ width: `${widthCh}ch` }}
                          onClick={() => toggleConsequenceEditing(consequenceId, true, consequence.title || '', isCompleted)}
                        >
                          {plainDraft ? plainDraft : <span className="text-muted-foreground italic">{t('clickToAddText')}</span>}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground shrink-0">
                        <CopyButton value={plainDraft} title={t('copy') || 'Copy'} />
                        {!readOnly && (isEditingConsequence ? (
                          <>
                            <Button size="sm" className="h-6 px-2 text-[10px] bg-success text-success-foreground hover:bg-success/90" onClick={() => handleConsequenceSave(consequenceId)}>
                              <Check size={10} className="mr-1" />{t('save')}
                            </Button>
                            <Button size="sm" className="h-6 px-2 text-[10px] bg-error text-error-foreground hover:bg-error/90" onClick={() => handleConsequenceCancel(consequenceId)}>
                              <X size={10} className="mr-1" />{t('cancel')}
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" className="h-6 px-2 text-[10px] bg-error text-error-foreground hover:bg-error/90" onClick={async () => { 
                            const confirmed = await dialog.confirm({
                              message: t('confirmDeleteConsequence') || 'Rimuovere questa conseguenza?',
                              type: 'warning'
                            })
                            if (confirmed) { 
                              dispatch({ type: 'DELETE_CONSEQUENCE', payload: { answerId: node.id, consequenceId } } as any) 
                            } 
                          }}>
                            <Trash size={10} className="mr-1" />{t('delete')}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      
      <div className="shrink-0 flex items-center h-6">
        <CopyButton value={editText || ''} title={t('copy') || 'Copy'} />
      </div>
    </div>
  )
}

// Helper function to get all nodes in flat order with metadata
function getFlatNodeList(rootQuestionId: string, qaNodes: Record<string, QANode>): Array<{ node: QANode, parentId?: string, type: 'question' | 'answer', level: number }> {
  const result: Array<{ node: QANode, parentId?: string, type: 'question' | 'answer', level: number }> = []
  
  function traverse(nodeId: string, parentId?: string, level: number = 0) {
    const node = qaNodes[nodeId]
    if (!node) return
    
    result.push({ node, parentId, type: node.type, level })
    
    if (node.type === 'question' && node.answerId) {
      traverse(node.answerId, nodeId, level)
    } else if (node.type === 'answer') {
      node.followUpQuestionIds.forEach(questionId => {
        traverse(questionId, nodeId, level + 1)
      })
    }
  }
  
  traverse(rootQuestionId)
  return result
}

interface FlatQAListProps {
  rootQuestionId: string
  qaNodes: Record<string, QANode>
  dispatch: React.Dispatch<any>
  readOnly?: boolean
  registerSaveHandler: (fn: () => void) => void
  unregisterSaveHandler: (fn: () => void) => void
  registerCancelHandler: (fn: () => void) => void
  unregisterCancelHandler: (fn: () => void) => void
}

function FlatQAList({ rootQuestionId, qaNodes, dispatch, readOnly = false, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: FlatQAListProps) {
  const flatNodes = getFlatNodeList(rootQuestionId, qaNodes)
  const lastQuestionId = [...flatNodes]
    .reverse()
    .find((n) => n.node.type === 'question')?.node.id

  const lastNode = flatNodes[flatNodes.length - 1]
  return (
    <div className={cn("space-y-3")}> 
      {flatNodes.map(({ node, parentId, level }) => (
        <FlatQANode 
          key={node.id}
          node={node}
          parentId={parentId}
          level={level}
          qaNodes={qaNodes}
          dispatch={dispatch}
          lastQuestionId={lastQuestionId}
          readOnly={readOnly}
          registerSaveHandler={registerSaveHandler}
          unregisterSaveHandler={unregisterSaveHandler}
          registerCancelHandler={registerCancelHandler}
          unregisterCancelHandler={unregisterCancelHandler}
        />
      ))}
    </div>
  )
}

interface FlatQANodeProps {
  node: QANode
  parentId?: string
  level: number
  qaNodes: Record<string, QANode>
  dispatch: React.Dispatch<any>
  lastQuestionId?: string
  readOnly?: boolean
  registerSaveHandler: (fn: () => void) => void
  unregisterSaveHandler: (fn: () => void) => void
  registerCancelHandler: (fn: () => void) => void
  unregisterCancelHandler: (fn: () => void) => void
}

function FlatQANode({ node, parentId, level, qaNodes, dispatch, lastQuestionId, readOnly = false, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler }: FlatQANodeProps) {
  const { t } = useTranslation()
  const { state } = useApp()
  const dialog = useDialog()
  const verboseMode = state.settings?.verboseMode ?? true
  const [isEditing, setIsEditing] = useState(!readOnly && !node.text)
  const [editText, setEditText] = useState(node.text)
  const [editHtml, setEditHtml] = useState(node.text)
  const [viewHtml, setViewHtml] = useState(node.text)
  const isRootQuestion = node.type === 'question' && level === 0
  const isOpen = node.type === 'question' && isQuestionOpen(node.id, qaNodes)

  const handleSaveEdit = () => {
    const payloadHtml = (editHtml || '').trim()
    const hasText = (editText || '').trim().length > 0
    if (hasText) {
      if (node.type === 'question') {
        dispatch({ type: 'UPDATE_QUESTION', payload: { id: node.id, text: payloadHtml } })
      } else {
        dispatch({ type: 'UPDATE_ANSWER', payload: { id: node.id, text: payloadHtml } })
      }
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setEditText(node.text)
    setEditHtml(node.text)
    setViewHtml(node.text)
    setIsEditing(false)
  }

  useEffect(() => {
    if (!readOnly && isEditing) {
      const saveCb = () => handleSaveEdit()
      const cancelCb = () => handleCancelEdit()
      registerSaveHandler?.(saveCb)
      registerCancelHandler?.(cancelCb)
      return () => {
        unregisterSaveHandler?.(saveCb)
        unregisterCancelHandler?.(cancelCb)
      }
    }
  }, [isEditing, editText, readOnly, registerSaveHandler, unregisterSaveHandler, registerCancelHandler, unregisterCancelHandler])

  useEffect(() => {
    setViewHtml(node.text)
    setEditHtml(node.text)
    setEditText(() => {
      try { const doc = new DOMParser().parseFromString(node.text || '', 'text/html'); return doc.body.textContent || '' } catch { return node.text || '' }
    })
  }, [node.text])

  const createEmptyAnswer = () => {
    if (readOnly) return
    if (node.type === 'question' && !node.answerId) {
      dispatch({ type: 'CREATE_ANSWER', payload: { questionId: node.id, text: '', status: 'inconclusive' } })
    }
  }

  const createEmptyFollowUpQuestion = () => {
    if (readOnly) return
    if (node.type === 'answer') {
      dispatch({ type: 'CREATE_QUESTION', payload: { text: '', parentAnswerId: node.id } })
    }
  }

  const handleAddConsequence = () => {
    if (readOnly) return
    if (node.type === 'answer') {
      dispatch({ type: 'ADD_CONSEQUENCE', payload: { answerId: node.id, title: '' } })
    }
  }

  const handleStatusToggle = (checked: boolean) => {
    if (readOnly) return
    if (node.type === 'answer') {
      dispatch({ type: 'UPDATE_ANSWER', payload: { id: node.id, status: checked ? 'conclusive' : 'inconclusive' } })
    }
  }

  const getBackgroundClasses = () => {
    if (node.type === 'answer') {
      return node.status === 'conclusive' ? 'bg-success-soft border-success-border' : 'bg-error-soft border-error-border'
    } else if (node.type === 'question' && isOpen) {
      return 'bg-open-question border-open-question-border'
    }
    return 'bg-card border-border'
  }

  return (
    <div className={cn("rounded-lg border p-4 transition-colors", getBackgroundClasses())}>
      <div className="flex items-center gap-3 mb-3">
        {verboseMode && (
        <Badge
          variant="outline"
          className={cn(
            "text-base font-bold font-mono px-2.5 py-1.5",
            node.type === 'question' ? 'text-info border-info-border bg-info-soft' : 'text-success border-success-border bg-success-soft'
          )}
        >
          {node.type === 'question' ? t('domanda') : t('risposta')}
        </Badge>
        )}
        <div className="flex items-center gap-2">
          {node.type === 'question' && !node.answerId && !readOnly && (
            <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={createEmptyAnswer}>
              <Plus size={12} className="mr-1" />
              {t('addAnswer')}
            </Button>
          )}
          {node.type === 'answer' && parentId && parentId === lastQuestionId && !readOnly && (
            <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={createEmptyFollowUpQuestion}>
              <Plus size={12} className="mr-1" />
              {t('addFollowupQuestion')}
            </Button>
          )}
          {node.type === 'answer' && !readOnly && (
            <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={handleAddConsequence}>
              <Plus size={12} className="mr-1" />
              {t('addConsequence')}
            </Button>
          )}
          {node.type === 'question' && !readOnly && (
            <Button
              size="sm"
              variant="destructive"
              onClick={async () => {
                const confirmed = await dialog.confirm({
                  message: t('confirmDeleteQuestion') || 'Delete this question and its subtree?',
                  type: 'warning'
                })
                if (confirmed) {
                  dispatch({ type: 'DELETE_QUESTION', payload: { id: node.id } } as any)
                }
              }}
            >
              <Trash size={12} className="mr-1" />
              {t('delete')}
            </Button>
          )}
          {node.type === 'answer' && !readOnly && (
            <Button
              size="sm"
              variant="destructive"
              onClick={async () => {
                const confirmed = await dialog.confirm({
                  message: t('confirmDeleteAnswer') || 'Delete this answer and its subtree?',
                  type: 'warning'
                })
                if (confirmed) {
                  dispatch({ type: 'DELETE_ANSWER', payload: { id: node.id } } as any)
                }
              }}
            >
              <Trash size={12} className="mr-1" />
              {t('delete')}
            </Button>
          )}
          {node.type === 'answer' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={`answer-conclusive-${node.id}`}
                checked={node.status === 'conclusive'}
                disabled={readOnly}
                onCheckedChange={(checked) => handleStatusToggle(!!checked)}
                className="h-4 w-4"
              />
              {verboseMode && <span className="text-xs">{t('conclusive')}</span>}
            </div>
          )}
        </div>
      </div>

      {isEditing && !readOnly ? (
        <div
          className="space-y-2"
          onKeyDown={(e) => {
            if (readOnly) return
            if (e.key === 'Escape') {
              e.preventDefault()
              handleCancelEdit()
            }
          }}
        >
          <div className="flex items-center gap-2">
            <div className="shrink-0 flex items-center h-6">
              <CopyButton value={editText || ''} valueHtml={editHtml || ''} title={t('copy') || 'Copy'} />
            </div>
            <div className="flex-1">
              <LexicalRichTextEditor
                initialValue={editHtml}
                onChange={(html, plain) => { setEditHtml(html); setEditText(plain); setViewHtml(html) }}
                placeholder={`${t('enter' + (node.type === 'question' ? 'Question' : 'Answer'))}...`}
                autoFocus
                className="min-h-[2rem]"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={handleSaveEdit}>
              <Check size={12} className="mr-1" />
              {t('save')}
            </Button>
            <Button size="sm" className="bg-error text-error-foreground hover:bg-error/90" onClick={handleCancelEdit}>
              <X size={12} className="mr-1" />
              {t('cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={(e) => {
            if (readOnly) return
            const target = e.target as HTMLElement
            const anchor = target.closest('a')
            if (anchor) return
            setIsEditing(true)
          }}
          className={cn("text-sm p-2 rounded min-h-[2rem]", !readOnly && "cursor-pointer hover:bg-accent/20")}
        >
          <div className="flex items-center gap-2">
            <div className="shrink-0 flex items-center h-6" onClick={(e) => { e.stopPropagation() }}>
              <CopyButton
                value={(() => {
                  const source = viewHtml || ''
                  try { const doc = new DOMParser().parseFromString(source, 'text/html'); return doc.body.textContent || '' } catch { return String(source || '') }
                })()}
                valueHtml={viewHtml || ''}
                title={t('copy') || 'Copy'}
              />
            </div>
            <div className="flex-1">
              {(() => {
                const source = viewHtml || ''
                if ((source || '').trim().length === 0) return <span className="text-muted-foreground italic">{t('clickToAddText')}</span>
                return <div dangerouslySetInnerHTML={{ __html: source }} />
              })()}
            </div>
          </div>
        </div>
      )}

      {node.type === 'answer' && node.consequences.length > 0 && (
        <div className="mt-3 space-y-2">
          {verboseMode && (
          <div className="text-xs font-medium text-muted-foreground">{t('consequences')}:</div>
          )}
          {node.consequences.map((consequence) => (
            <EditableConsequenceRow
              key={consequence.id}
              answerId={node.id}
              consequence={consequence}
              dispatch={dispatch}
              readOnly={readOnly}
              registerSaveHandler={registerSaveHandler}
              unregisterSaveHandler={unregisterSaveHandler}
              registerCancelHandler={registerCancelHandler}
              unregisterCancelHandler={unregisterCancelHandler}
            />
          ))}
        </div>
      )}
    </div>
  )
}